"use client";
import { Colors } from "@/enums/Colors";
import { createTheme } from "@mui/material/styles";
import { Inter, Roboto_Mono } from "next/font/google";
import localFont from "next/font/local";

const roboto = Roboto_Mono({
  weight: ["300", "400", "500"],
  subsets: ["latin"],
});

const NewInter = localFont({
  src: [{ path: "./Fonts/InterVariable.woff2" }],
  weight: "400",
  style: "normal",
});

const inter = Inter({
  weight: ["300", "400", "500"],
  subsets: ["latin"],
});

export const customMuiTheme = createTheme({
  transitions: {
    duration: {
      enteringScreen: 300,
      leavingScreen: 300,
    },
  },
  palette: {
    primary: {
      main: Colors.WHITE,
      light: Colors.SKIN,
      dark: Colors.BLACK,
    },
    text: {
      primary: Colors.ELEGANT_BLACK,
    },
  },
  typography: {
    fontFamily: [NewInter.style.fontFamily, "Arial", "Helvetica", "sans-serif"].join(","),
    allVariants: {
      fontFeatureSettings: `"cv11" 1`,
    },
  },
  components: {
    MuiAvatar: {
      styleOverrides: {
        img: {
          objectFit: "contain",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          border: "none",
          margin: "0 1rem 0 1rem",
        },
        content: {
          margin: 0,
          alignItems: "center",
          minWidth: "50%",
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0 !important",
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          height: "2rem",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "1.3rem",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          fontWeight: 400,
        },
        h2: {
          fontSize: "1.5rem",
          fontWeight: 500,
        },
        h3: {
          fontSize: "1.2rem",
          fontWeight: 500,
        },
        h5: {
          fontSize: "1rem",
          fontWeight: 400,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          paddingLeft: 0,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          ":hover:not(.Mui-disabled, .Mui-error)::before": {
            borderBottom: "2px solid rgba(89, 107, 119, 1)",
          },
          "::before": {
            borderBottom: "1px solid rgba(89, 107, 119, 1)",
          },
        },
      },
    },
    MuiButton: {
      // Note: If you add a custom button variant you will need to add the typescript definition in Common.ts
      styleOverrides: {
        contained: {
          color: "#354B59",
          backgroundColor: "#E0E0E0",
          border: `1px solic ${Colors.STONE_GRAY}`,
          ":hover": {
            color: "#FFFFFF",
            backgroundColor: Colors.STONE_OCEAN_BLUE,
            border: `1px solid ${Colors.STONE_OCEAN_BLUE}`,
          },
        },
        text: {
          color: "#354B59",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: Colors.GREY,
          "&.Mui-focused": {
            color: Colors.GREY,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: Colors.BLACK,
          "&.Mui-checked": {
            color: Colors.LAZO_PURPLE,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        autoComplete: "off",
      },
      styleOverrides: {
        root: {
          margin: 0,
          "& .MuiOutlinedInput-notchedOutline": {
            border: `2px solid ${Colors.STONE_GRAY}`,
          },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              border: `2px solid ${Colors.STONE_GRAY}`,
            },
            "&.Mui-focused fieldset": {
              border: `2px solid ${Colors.STONE_GRAY}`,
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: "2%",
          "& .MuiOutlinedInput-notchedOutline": {
            border: `2px solid ${Colors.STONE_GRAY}`,
          },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              border: `2px solid ${Colors.STONE_GRAY}`,
            },
            "&.Mui-focused fieldset": {
              border: `2px solid ${Colors.STONE_GRAY}`,
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: Colors.GREY_65696C,
          boxShadow: "1px 1px 4px rgba(0, 0, 0, 0.2)",
        },
      },
    },
  },
});
